import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { pickFromDrive, OWL_DOC_MIMES } from '../lib/drivePicker.js';
import Icon from './Icon.jsx';

// Google Drive → the Owl. Same component on both surfaces via the scope prop:
// 'admin-client' (Admin → client → Integrations) | 'my' (Settings → Integrations).
// Flow: paste the service-account key once (write-only) → share files/folders
// with its email → paste the link here. Sheets become attached tables (askUpload);
// Docs/Slides/PDFs become searchable text the Owl quotes with the file name.
export default function DriveSourcesCard({ entityId, scope = 'my' }) {
  const get = () => (scope === 'admin-client' ? api.adminDriveView(entityId) : api.myDriveView(entityId));
  const run = (fn, ...args) => (scope === 'admin-client' ? api[`adminDrive${fn}`](entityId, ...args) : api[`myDrive${fn}`](entityId, ...args));

  const [view, setView] = useState(null);
  const [denied, setDenied] = useState(false);
  const [keyJson, setKeyJson] = useState('');
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const cardRef = useRef(null);
  const [flash, setFlash] = useState(''); // 'connected' | 'denied' — coming back from Google
  const [adding, setAdding] = useState([]); // names picked but not yet stored — instant feedback
  const [folder, setFolder] = useState('');  // '' = all, '_none' = unfiled, else a folder id
  const [filterBy, setFilterBy] = useState('folder'); // folder | event
  const [eventFilter, setEventFilter] = useState('');

  useEffect(() => { get().then(setView).catch(() => setDenied(true)); }, [entityId, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  // Returning from Google lands on ?drive=connected|denied. Without this the
  // reader is dropped at the TOP of Settings with no sign anything happened —
  // so say it plainly and scroll them onto the card they started from. The
  // param is stripped straight away, so a refresh doesn't re-announce it.
  useEffect(() => {
    if (!view) return undefined;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('drive');
    if (!outcome) return undefined;
    setFlash(outcome);
    params.delete('drive');
    window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`);
    const t = setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
    return () => clearTimeout(t);
  }, [view]);

  // Ingestion runs in the background (a PDF is transcribed by AI, a folder walks
  // every child), so the card follows it: while any source is 'pending' it
  // re-reads itself until everything settles. Stops on its own — no timers left
  // running once the work is done.
  const syncing = (view?.sources || []).some((s) => s.status === 'pending');
  useEffect(() => {
    if (!syncing) return undefined;
    const t = setTimeout(() => { get().then(setView).catch(() => {}); }, 3000);
    return () => clearTimeout(t);
  }, [syncing, view]); // eslint-disable-line react-hooks/exhaustive-deps

  if (denied || !view) return null;

  const act = async (label, fn) => {
    setBusy(label); setError('');
    try { setView(await fn()); }
    catch (e) { setError(e.message || 'Something went wrong.'); }
    setBusy('');
  };

  // ── One-click OAuth + the Google Picker ──
  const oauth = view.oauth || {};
  const isAdminScope = scope === 'admin-client';
  const connectGoogle = async () => {
    setBusy('oauth'); setError('');
    try {
      const r = scope === 'admin-client' ? await api.adminDriveOauthStart(entityId, window.location.pathname + window.location.search) : await api.myDriveOauthStart(entityId, window.location.pathname + window.location.search);
      window.location.href = r.url;
    } catch (e) { setError(e.message); setBusy(''); }
  };
  const disconnectGoogle = (conn) => window.confirm(conn && !conn.mine
    ? `Disconnect ${conn.email}? Documents only their Google account can see will stop syncing.`
    : 'Disconnect your Google account? Documents only you could see stop syncing (they stay listed until removed).')
    && act('oauth', () => (scope === 'admin-client' ? api.adminDriveOauthDisconnect(entityId, conn?.id) : api.myDriveOauthDisconnect(entityId, conn?.id)));
  const openPicker = async (connId = '') => {
    setBusy('picker'); setError('');
    try {
      let usedConn = connId;
      const docs = await pickFromDrive({
        getToken: async () => {
          const t = await (scope === 'admin-client' ? api.adminDrivePickerToken(entityId, connId) : api.myDrivePickerToken(entityId, connId));
          usedConn = t.connId || connId;
          return t;
        },
        mimeTypes: OWL_DOC_MIMES,
      });
      if (docs.length) {
        // Show the picked files straight away — the server answers before the
        // content is read, and a name with a spinner beats an empty card.
        setAdding(docs.map((d) => d.name || 'File'));
        const add1 = (id) => (scope === 'admin-client' ? api.adminDriveAddSource(entityId, { fileId: id, connId: usedConn }) : api.myDriveAddSource(entityId, { fileId: id, connId: usedConn }));
        const results = await Promise.allSettled(docs.map((d) => add1(d.id)));
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length) setError(failed.map((f) => f.reason?.message || 'Could not add a file').join(' · '));
        setAdding([]);
        setView(await get());
      }
    } catch (e) { setError(e.message); setAdding([]); }
    setBusy('');
  };
  // ── folders: categorise documents, and say which EVENT a set belongs to ───────
  const folders = view.folders || [];
  const events = view.events || [];
  const activeFolder = folders.find((f) => f.id === folder) || null;
  const eventName = (id) => (events.find((e) => e.id === id) || {}).name || '';
  const folderEvents = (f) => (f.suiteIds && f.suiteIds.length ? f.suiteIds : (f.suiteId ? [f.suiteId] : []));
  const srcEvents = (s2) => (s2.eventIds || []);
  const matches = (s2) => (filterBy === 'event'
    ? (!eventFilter || (eventFilter === '_none' ? srcEvents(s2).length === 0 : srcEvents(s2).includes(eventFilter)))
    : (folder === '' ? true : folder === '_none' ? !s2.folderId : s2.folderId === folder));
  const runFolder = (fid, body) => (scope === 'admin-client' ? api.adminDriveSaveFolder(entityId, fid, body) : api.myDriveSaveFolder(entityId, fid, body));
  const newFolder = () => {
    const name = (window.prompt('Name this folder (e.g. “Ultra 2026”, “Contracts”)') || '').trim();
    if (name) act('folder', () => runFolder('', { name }));
  };
  const renameFolder = () => {
    const name = (window.prompt('Rename folder', activeFolder.name) || '').trim();
    if (name) act('folder', () => runFolder(activeFolder.id, { name }));
  };
  const deleteFolder = () => window.confirm(`Delete the folder “${activeFolder.name}”? The documents in it stay — they just become unfiled.`)
    && act('folder', async () => { const v = await (scope === 'admin-client' ? api.adminDriveDeleteFolder(entityId, activeFolder.id) : api.myDriveDeleteFolder(entityId, activeFolder.id)); setFolder(''); return v; });
  const toggleFolderEvent = (suiteId) => {
    const cur = folderEvents(activeFolder);
    const next = cur.includes(suiteId) ? cur.filter((x) => x !== suiteId) : [...cur, suiteId];
    return act('folder', () => runFolder(activeFolder.id, { suiteIds: next }));
  };
  const fileInto = (s, fid) => act(s.id, () => run('UpdateSource', s.id, { folderId: fid }));

  const saveKey = () => act('key', async () => { const v = await run('SetKey', { serviceAccountJson: keyJson }); setKeyJson(''); setShowKeyForm(false); return v; });
  const clearKey = () => window.confirm('Disconnect Google Drive? Added files stop syncing (they stay until removed).') && act('key', () => run('SetKey', { clear: true }));
  const add = () => act('add', async () => { const v = await run('AddSource', { link: link.trim() }); setLink(''); return v; });
  const copyEmail = () => {
    (navigator.clipboard?.writeText(view.saEmail) || Promise.reject()).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); })
      .catch(() => window.prompt('Copy this email:', view.saEmail));
  };

  const STATUS = { ok: ['✓ synced', 'var(--success, #16a34a)'], partial: ['⚠︎ partial', '#d97706'], error: ['✕ error', 'var(--danger, #dc2626)'], unsupported: ['unsupported', 'var(--muted)'], pending: ['reading…', 'var(--muted)'] };
  // Name the wait: a PDF is read by AI and a folder walks its files, both of
  // which take real time — "reading…" with no idea why looks stuck.
  const pendingLabel = (s) => (s.kind === 'folder' ? 'reading files…' : /pdf/.test(s.mime) ? 'reading the PDF (AI)…' : 'reading…');
  const chip = (s, src) => {
    const [t, c] = STATUS[s] || [s, 'var(--muted)'];
    const label = s === 'pending' && src ? pendingLabel(src) : t;
    return <span style={{ fontSize: 11.5, fontWeight: 700, color: c, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>{s === 'pending' && <Spinner />}{label}</span>;
  };
  const icon = (s) => (s.kind === 'folder' ? '📁' : /spreadsheet|csv/.test(s.mime) ? '📊' : /pdf/.test(s.mime) ? '📕' : '📄');

  return (
    <div style={card} ref={cardRef}>
      <div style={title}>🗂️ Google Drive — files the Owl can read</div>
      {flash && (
        <div style={{ borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 13, lineHeight: 1.45, border: `1.5px solid ${flash === 'connected' ? 'var(--success, #16a34a)' : 'var(--hairline)'}`, background: flash === 'connected' ? 'rgba(22,163,74,0.08)' : 'transparent', color: flash === 'connected' ? 'var(--success, #16a34a)' : 'var(--muted)' }}>
          {flash === 'connected'
            ? <><b>✓ Connected{oauth.email ? ` as ${oauth.email}` : ''}.</b> Now tap <b>Pick files or folders</b> below to choose what the Owl may read.</>
            : <>Connection cancelled — nothing changed. Tap <b>Connect with Google</b> to try again.</>}
        </div>
      )}
      <p style={sub}>
        Share budgets, plans or contracts with the Owl: it answers questions from them alongside your live data.
        Anything you add here is <b>readable by everyone on this account</b> unless you mark it private.
        Sheets become queryable tables; Docs, Slides and PDFs become searchable text the Owl quotes by name.
      </p>

      {oauth.connected ? (
        <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(oauth.connections || []).map((c) => (
            <div key={c.id} style={{ ...row, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 220px', fontSize: 13 }}>
                <b>{c.shared ? 'Account Google' : 'Connected as'} {c.email || 'Google'}{c.mine ? ' · you' : ''}</b>
                <div style={{ fontSize: 12, color: c.error ? 'var(--danger, #dc2626)' : 'var(--muted)', marginTop: 2 }}>
                  {c.error || (c.shared
                    ? 'The company account — anyone here can add files from it, and everyone can read what it holds. Point it at your shared team folder and turn watch on: files dropped in Drive arrive on their own.'
                    : c.mine
                      ? 'Your own account. Pick the files or folders the Owl may read — it only ever sees what you pick.'
                      : 'A colleague\'s account. Documents they picked are readable by everyone here; only they can pick more.')}
                </div>
                {(c.mine || isAdminScope) && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', marginTop: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!c.shared} onChange={(e) => act('oauth', () => (scope === 'admin-client' ? api.adminDriveSetConnectionShared(entityId, c.id, e.target.checked) : api.myDriveSetConnectionShared(entityId, c.id, e.target.checked)))} />
                    Use as the account&apos;s Google — everyone here can add files from it
                  </label>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                {c.canPick && (c.error && c.mine
                  ? <button style={btn} disabled={busy === 'oauth'} onClick={connectGoogle}>Reconnect</button>
                  : <button style={btn} disabled={busy === 'picker' || busy === 'add'} onClick={() => openPicker(c.id)}>{busy === 'picker' ? 'Opening…' : '📂 Pick files or folders'}</button>)}
                {(c.mine || isAdminScope) && <button style={dangerBtn} onClick={() => disconnectGoogle(c)}>Disconnect</button>}
              </div>
            </div>
          ))}
          {/* Anyone else on the account connects their OWN Google — it never
              replaces a colleague's, and each person's picked files keep working. */}
          {!oauth.mine && (
            <button style={btn} disabled={busy === 'oauth'} onClick={connectGoogle}>{busy === 'oauth' ? 'Opening…' : 'Connect my Google account'}</button>
          )}
        </div>
      ) : !showKeyForm && (
        // One-click is offered even when a service-account connection already
        // exists (a client's own key, or Howler's platform-wide one) — otherwise
        // everyone already on the fallback would never see the easy path.
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {oauth.available && <button style={btn} disabled={busy === 'oauth'} onClick={connectGoogle}>{busy === 'oauth' ? 'Opening…' : 'Connect with Google'}</button>}
            {!view.configured && <button style={oauth.available ? ghostBtn : btn} onClick={() => setShowKeyForm(true)}>{oauth.available ? 'Use a service account instead' : 'Connect Google Drive'}</button>}
          </div>
          {oauth.available && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
              Sign in once and pick the files or folders the Owl may read — nothing else in your Drive is visible to Pulse.
              {view.configured && view.mode === 'sa' ? ' The share-with-an-email setup below keeps working either way.' : ''}
            </div>
          )}
        </div>
      )}
      {(showKeyForm || (!view.configured && showKeyForm)) && (
        <div style={{ marginBottom: 12 }}>
          <p style={sub}>Paste a Google <b>service-account JSON key</b> (Google Cloud → IAM → Service accounts → Keys). It's stored write-only and never shown again.</p>
          <textarea value={keyJson} onChange={(e) => setKeyJson(e.target.value)} rows={4} placeholder='{"type":"service_account","client_email":"…","private_key":"…"}' style={{ ...input, width: '100%', fontFamily: 'monospace', fontSize: 12 }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button style={btn} disabled={busy === 'key' || !keyJson.trim()} onClick={saveKey}>{busy === 'key' ? 'Saving…' : 'Save key'}</button>
            <button style={ghostBtn} onClick={() => { setShowKeyForm(false); setKeyJson(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {view.configured && view.mode === 'sa' && (
        <>
          <div style={{ ...row, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ minWidth: 0, flex: '1 1 240px' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>
                1 · Share files or folders with{view.envFallback ? ' (platform connection)' : ''}:
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', userSelect: 'all' }}>{view.saEmail}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button style={ghostBtn} onClick={copyEmail}>{copied ? 'Copied ✓' : 'Copy email'}</button>
              {!view.envFallback && view.keySet && <button style={dangerBtn} onClick={clearKey}>Disconnect</button>}
              {view.envFallback && <button style={ghostBtn} onClick={() => setShowKeyForm(true)}>Use own key</button>}
            </div>
          </div>

          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>2 · Paste the link — a Sheet, Doc, Slides, PDF or a whole folder (folders keep themselves in sync):</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <input style={input} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://drive.google.com/…" onKeyDown={(e) => e.key === 'Enter' && link.trim() && add()} />
            <button style={btn} disabled={!link.trim() || busy === 'add'} onClick={add}>{busy === 'add' ? 'Adding…' : 'Add'}</button>
          </div>
        </>
      )}
      {error && <p style={{ ...sub, color: 'var(--danger, #dc2626)' }}>{error}</p>}

      {(view.sources || []).length > 0 && (
        <>
          {events.length > 0 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, fontSize: 12, color: 'var(--muted)' }}>
              <span>Show by</span>
              {[['folder', 'Folder'], ['event', 'Event']].map(([k, lbl]) => (
                <button key={k} onClick={() => setFilterBy(k)} style={{ ...chip, padding: '3px 10px', minHeight: 28, ...(filterBy === k ? chipOn : {}) }}>{lbl}</button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            {filterBy === 'event' ? (
              <>
                <button onClick={() => setEventFilter('')} style={{ ...chip, ...(eventFilter === '' ? chipOn : {}) }}>All</button>
                {events.map((e) => {
                  const n = (view.sources || []).filter((s) => srcEvents(s).includes(e.id)).length;
                  return n === 0 ? null : (
                    <button key={e.id} onClick={() => setEventFilter(e.id)} style={{ ...chip, ...(eventFilter === e.id ? chipOn : {}) }}>{e.name} · {n}</button>
                  );
                })}
                {(view.sources || []).some((s) => srcEvents(s).length === 0) && (
                  <button onClick={() => setEventFilter('_none')} style={{ ...chip, ...(eventFilter === '_none' ? chipOn : {}) }}>General · {(view.sources || []).filter((s) => srcEvents(s).length === 0).length}</button>
                )}
              </>
            ) : (
              <>
                <button onClick={() => setFolder('')} style={{ ...chip, ...(folder === '' ? chipOn : {}) }}>All</button>
                {folders.map((f) => (
                  <button key={f.id} onClick={() => setFolder(f.id)} style={{ ...chip, ...(folder === f.id ? chipOn : {}) }}>
                    {f.name}{folderEvents(f).length ? ` · ${folderEvents(f).map(eventName).filter(Boolean).join(', ')}` : ''} · {(view.sources || []).filter((s) => s.folderId === f.id).length}
                  </button>
                ))}
                {folders.length > 0 && (view.sources || []).some((s) => !s.folderId) && (
                  <button onClick={() => setFolder('_none')} style={{ ...chip, ...(folder === '_none' ? chipOn : {}) }}>Unfiled · {(view.sources || []).filter((s) => !s.folderId).length}</button>
                )}
                <button onClick={newFolder} style={{ ...chip, color: 'var(--muted)' }}>＋ Folder</button>
              </>
            )}
          </div>
        </>
      )}
      {activeFolder && filterBy === 'folder' && (
        <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: '1 1 auto' }}>Events this folder serves — tick any number:</span>
            <button style={ghostBtn} onClick={renameFolder}>Rename</button>
            <button style={dangerBtn} onClick={deleteFolder}>Delete folder</button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {events.map((e) => (
              <button key={e.id} onClick={() => toggleFolderEvent(e.id)} style={{ ...chip, ...(folderEvents(activeFolder).includes(e.id) ? chipOn : {}) }}>{e.name}</button>
            ))}
            {events.length === 0 && <span style={{ fontSize: 12, color: 'var(--muted)' }}>No events on this account yet.</span>}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
            {folderEvents(activeFolder).length
              ? `The Owl reads these when ${folderEvents(activeFolder).map(eventName).filter(Boolean).join(' or ')} is in focus — plus anything general. Naming a document still finds it any time.`
              : 'No events ticked: these are general documents and answer for any event.'}
          </div>
        </div>
      )}
      {adding.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
          {adding.map((name, i) => (
            <div key={`adding-${i}`} style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, opacity: 0.75 }}>
              <Icon name="notes" size={15} />
              <span style={{ fontSize: 13.5, fontWeight: 600, flex: '1 1 140px', minWidth: 0, overflowWrap: 'anywhere' }}>{name}</span>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Spinner />adding…</span>
            </div>
          ))}
        </div>
      )}
      {(view.sources || []).length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {view.sources.filter(matches).map((s) => (
            <div key={s.id} style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>{icon(s)}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, flex: '1 1 140px', minWidth: 0, overflowWrap: 'anywhere' }}>{s.name || s.fileId}</span>
                {chip(s.status, s)}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {s.kind === 'folder' && (
                    <button style={ghostBtn} title="Auto-sync hourly" onClick={() => act(s.id, () => run('UpdateSource', s.id, { watch: !s.watch }))}>{s.watch ? '👁 watching' : 'watch'}</button>
                  )}
                  <button
                    style={{ ...ghostBtn, color: s.visibility === 'private' ? 'var(--brand)' : 'var(--muted)' }}
                    title={s.visibility === 'private' ? 'Private to you — tap to share with the account' : 'Shared with everyone on this account — tap to make it private to you'}
                    onClick={() => act(s.id, () => run('UpdateSource', s.id, { visibility: s.visibility === 'private' ? 'shared' : 'private' }))}
                  >{s.visibility === 'private' ? 'private' : 'shared'}</button>
                  {folders.length > 0 && (
                    <select value={s.folderId || ''} onChange={(e) => fileInto(s, e.target.value)} title="File this in a folder" style={{ ...ghostBtn, padding: '4px 8px', maxWidth: 130 }}>
                      <option value="">Unfiled</option>
                      {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  )}
                  <button style={ghostBtn} disabled={busy === s.id} onClick={() => act(s.id, () => run('SyncSource', s.id))}>{busy === s.id ? '…' : '↻'}</button>
                  <button style={dangerBtn} onClick={() => window.confirm(`Remove "${s.name}"? The Owl forgets it immediately.`) && act(s.id, () => run('RemoveSource', s.id))}>✕</button>
                </div>
              </div>
              {s.error && <div style={{ fontSize: 12, color: 'var(--danger, #dc2626)', marginTop: 4 }}>{s.error}</div>}
              {s.kind === 'folder' && (s.files || []).length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {s.files.map((f) => (
                    <div key={f.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: f.status === 'error' ? 'var(--danger, #dc2626)' : 'var(--muted)' }}>
                      <span>{/pdf/.test(f.mime) ? '📕' : f.kind === 'table' ? '📊' : '📄'}</span>
                      <span style={{ overflowWrap: 'anywhere' }}>{f.name}</span>
                      {f.status === 'error' && <span title={f.error}>✕</span>}
                    </div>
                  ))}
                </div>
              )}
              {s.lastSynced && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>Last synced {new Date(s.lastSynced).toLocaleString()}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A small spinner — a status that moves reads as progress; static text reads as stuck.
function Spinner() {
  return (
    <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', display: 'inline-block', animation: 'drive-spin 0.8s linear infinite' }}>
      <style>{'@keyframes drive-spin{to{transform:rotate(360deg)}}'}</style>
    </span>
  );
}

const chip = { border: '1px solid var(--hairline)', background: 'transparent', color: 'inherit', borderRadius: 980, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 32 };
const chipOn = { background: 'var(--brand)', color: '#fff', borderColor: 'var(--brand)' };

const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 16, marginTop: 16 };
const title = { fontSize: 14, fontWeight: 700, marginBottom: 6 };
const sub = { fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' };
const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px' };
const input = { flex: '1 1 220px', minWidth: 0, border: '1px solid var(--hairline)', borderRadius: 8, padding: '9px 12px', fontSize: 13.5, background: 'var(--card)', color: 'inherit' };
const btn = { border: '1px solid var(--hairline)', background: 'var(--brand)', color: '#fff', borderRadius: 980, padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', minHeight: 40 };
const ghostBtn = { ...btn, background: 'transparent', color: 'var(--muted)' };
const dangerBtn = { ...ghostBtn, color: 'var(--danger, #dc2626)', flexShrink: 0 };
